import React, { useState, useEffect } from "react";
import moment from "moment";
import { Table, PageHeader, Button, Card, Form, Select, Row, Col, Input, Tag } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import DataFetchHookDomainMonitor from "../../helper/DataFetchHookDomainMonitor";
import PermissionsRequired from "../../helper/PermissionsRequired";
import CustomPaginationV2 from "../../helper/Table/CustomPaginationV2";
import SortOrder from "../../helper/Table/SortOrder";
import UserRelationsModal from "./UserRelationsModal";

// Domain Monitor users list — dashboard counterpart of txshield admin domain_monitor_users.php.
// Read-only, SYSADMIN only. Data comes straight from Domain Monitor GET /v1/users using the
// dashboard dm_token (sysadmin scope sees all users).
const DATA_URL = "/users";
const DEFAULT_SORT_FIELD = "created_at";
const ROLES = ["superadmin", "client", "reseller", "merchant"];
const STATUSES = ["active", "inactive"];
const STATUS_COLORS = { active: "success", inactive: "default", pending_verification: "warning" };
const RELATION_BY_ROLE = { reseller: "merchants", client: "resellers" };

const initialRequest = {
    page: 1,
    size: 20,
    order: "DESC",
    sort_field: DEFAULT_SORT_FIELD,
};

const initFormData = {
    field: "email",
    keyword: null,
    role: undefined,
    status: undefined,
};

const DomainMonitorUsers = (props) => {
    const { t } = props;
    const [form] = Form.useForm();
    const [{ data, totalResult, isLoading, requestData }, , setRequestData] = DataFetchHookDomainMonitor(
        DATA_URL,
        "GET",
        initialRequest
    );
    const [currentPage, setCurrentPage] = useState(1);
    const [sortedInfo, setSortedInfo] = useState({ columnKey: DEFAULT_SORT_FIELD, order: "descend" });
    const [relation, setRelation] = useState({ visible: false, type: null, user: null });

    useEffect(() => {
        form.setFieldsValue(initFormData);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (requestData.page && parseInt(requestData.page) !== parseInt(currentPage)) {
            setCurrentPage(parseInt(requestData.page));
        }
    }, [requestData.page, currentPage]);

    const handleChange = (pagination, filters, sorter) => {
        // Pagination changes are handled by CustomPaginationV2; only react to sorting here.
        if (pagination.current === currentPage && pagination.pageSize === requestData.size) {
            setSortedInfo(sorter);
            if (sorter && sorter.columnKey) {
                setCurrentPage(1);
                setRequestData({
                    ...requestData,
                    page: 1,
                    sort_field: sorter.order ? sorter.columnKey : DEFAULT_SORT_FIELD,
                    order: sorter.order === "ascend" ? "ASC" : "DESC",
                });
            }
        }
    };

    const onFinish = (values) => {
        const keyword = values.keyword ? values.keyword.trim() : null;
        setCurrentPage(1);
        setRequestData({
            ...requestData,
            email: values.field === "email" && keyword ? keyword : null,
            role: values.role || null,
            status: values.status || null,
            page: 1,
        });
    };

    const clearAll = () => {
        form.setFieldsValue(initFormData);
        setSortedInfo({ columnKey: DEFAULT_SORT_FIELD, order: "descend" });
        setCurrentPage(1);
        setRequestData({ ...initialRequest, size: parseInt(requestData.size) || initialRequest.size });
    };

    const openRelation = (user) => {
        setRelation({ visible: true, type: RELATION_BY_ROLE[user.role], user });
    };

    const openDomains = (user) => {
        setRelation({ visible: true, type: "domains", user });
    };

    const viewLink = (onClick) => (
        <Button type="link" size="small" style={{ padding: 0 }} onClick={onClick}>
            {t("domain_monitor.users_view")}
        </Button>
    );

    const columns = [
        {
            dataIndex: "id",
            key: "id",
            title: t("domain_monitor.users_id"),
            width: 300,
        },
        {
            dataIndex: "email",
            key: "email",
            title: t("domain_monitor.users_email"),
            sorter: true,
            sortOrder: SortOrder(sortedInfo, "email"),
        },
        {
            dataIndex: "name",
            key: "name",
            title: t("domain_monitor.users_name"),
            sorter: true,
            sortOrder: SortOrder(sortedInfo, "name"),
        },
        {
            dataIndex: "role",
            key: "role",
            title: t("domain_monitor.users_role"),
            sorter: true,
            sortOrder: SortOrder(sortedInfo, "role"),
            render: (role) => t(`domain_monitor.users_role_${role}`, role),
        },
        {
            dataIndex: "status",
            key: "status",
            title: t("domain_monitor.users_status"),
            sorter: true,
            sortOrder: SortOrder(sortedInfo, "status"),
            render: (status) => (
                <Tag color={STATUS_COLORS[status] || "default"}>
                    {t(`domain_monitor.users_status_${status}`, status)}
                </Tag>
            ),
        },
        {
            dataIndex: "createdAt",
            key: "created_at",
            title: t("domain_monitor.users_created_at"),
            width: 180,
            sorter: true,
            sortOrder: SortOrder(sortedInfo, "created_at"),
            render: (createdAt) => (createdAt ? moment(createdAt).format("YYYY-MM-DD HH:mm:ss") : ""),
        },
        {
            key: "merchants",
            title: t("domain_monitor.users_merchants"),
            width: 110,
            render: (text, record) => (record.role === "reseller" ? viewLink(() => openRelation(record)) : null),
        },
        {
            key: "resellers",
            title: t("domain_monitor.users_resellers"),
            width: 110,
            render: (text, record) => (record.role === "client" ? viewLink(() => openRelation(record)) : null),
        },
        {
            key: "domains",
            title: t("domain_monitor.users_domains"),
            width: 110,
            fixed: "right",
            // Superadmins own no domains and have no merchants under them.
            render: (text, record) => (record.role !== "superadmin" ? viewLink(() => openDomains(record)) : null),
        },
    ];

    const AdvancedSearchForm = () => (
        <div className="search-wrap">
            <Card title={t("tables.actions.search")} size="small">
                <Form form={form} size="large" name="dm_users_search" className="ant-advanced-search-form" onFinish={onFinish}>
                    <Row gutter={[16, 16]}>
                        <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} xl={{ span: 4 }} xxl={{ span: 3 }}>
                            <Form.Item name="field">
                                <Select style={{ width: "100%" }} placeholder={t("tables.fields.field")}>
                                    <Select.Option value="email">{t("domain_monitor.users_email")}</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 6 }} xl={{ span: 6 }} xxl={{ span: 7 }}>
                            <Form.Item name="keyword">
                                <Input placeholder={t("tables.fields.keyword")} />
                            </Form.Item>
                        </Col>
                        <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 6 }} lg={{ span: 6 }} xl={{ span: 4 }}>
                            <Form.Item name="role">
                                <Select allowClear placeholder={t("domain_monitor.users_role")} style={{ width: "100%" }}>
                                    {ROLES.map((role) => (
                                        <Select.Option key={role} value={role}>{t(`domain_monitor.users_role_${role}`, role)}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 6 }} lg={{ span: 6 }} xl={{ span: 4 }}>
                            <Form.Item name="status">
                                <Select allowClear placeholder={t("domain_monitor.users_status")} style={{ width: "100%" }}>
                                    {STATUSES.map((status) => (
                                        <Select.Option key={status} value={status}>{t(`domain_monitor.users_status_${status}`, status)}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 6 }} lg={{ span: 6 }} xl={{ span: 3 }}>
                            <Form.Item>
                                <Button type="secondary" onClick={clearAll} block>
                                    {t("tables.actions.clear_all")}
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 6 }} lg={{ span: 6 }} xl={{ span: 3 }}>
                            <Form.Item>
                                <Button type="primary" htmlType="submit" block>
                                    <SearchOutlined /> {t("tables.actions.search")}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>
        </div>
    );

    const getHeaderTable = () => (
        <div className="custom-table--header d-flex justify-content-between align-items-center">
            <h3 className="table-title">{t("domain_monitor.users_title")}</h3>
        </div>
    );

    const paginationConfig = CustomPaginationV2({
        totalResult: parseInt(totalResult) || 0,
        currentPage: parseInt(currentPage) || 1,
        requestData: {
            ...requestData,
            page: parseInt(requestData.page) || 1,
            size: parseInt(requestData.size) || initialRequest.size,
        },
        setRequestData,
        setCurrentPage,
    });

    return PermissionsRequired("SYSADMIN") ? (
        <div>
            <PageHeader title={t("domain_monitor.users_title")} subTitle={t("domain_monitor.page_subtitle")} />
            {AdvancedSearchForm()}
            <Table
                loading={isLoading}
                columns={columns}
                title={() => getHeaderTable()}
                rowKey="id"
                className="table-v3 custom-pagination"
                size="small"
                scroll={{ x: "auto" }}
                dataSource={Array.isArray(data.datalist) ? data.datalist : []}
                onChange={handleChange}
                pagination={paginationConfig}
            />
            <UserRelationsModal
                t={t}
                visible={relation.visible}
                type={relation.type}
                user={relation.user}
                onClose={() => setRelation({ visible: false, type: null, user: null })}
            />
        </div>
    ) : null;
};

export default DomainMonitorUsers;
